import * as React from 'react';
import { View } from 'react-native';
import { runOnJS, useAnimatedProps, useDerivedValue } from 'react-native-reanimated';
import { rem, signPlusSymbolWidth, symbolWidth } from 'design-system';
import Logo from 'design-system/Logo';
import Spacer from 'design-system/Spacer';
import Text, { styles as textStyles } from 'design-system/Text';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import { CurrencyAmountText } from 'design-system/CurrencyAmountText';
import IconLeaf from 'design-system/icons/IconLeaf';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatAmountWorklet } from 'utils/formatting';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useAppSelector } from 'store/hooks';
import useColors from 'hooks/useColors';
import useStockMeta from '../hooks/useStockMeta';
import { useCurrentPrice } from '../hooks/selectors';
import StockInfoPriceChangeView from './StockInfoPriceChangeView';
import { isAndroid, isWeb } from '../../../constants';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
const getFallbackInfo = (fallbackAmount, balanceSymbol) => {
    const amount = formatAmount(Math.abs(fallbackAmount), undefined, 2);
    const sign = fallbackAmount < 0 ? '-' : '';
    const [decimal, fraction] = amount.split('.');
    return {
        decimal,
        fraction: `.${fraction}`,
        currencyAndSign: `${sign}${balanceSymbol}`,
        currencyAndSignWidth: sign ? signPlusSymbolWidth : symbolWidth,
    };
};
const StockInfoView = ({ assetId, priceTrend, selectedPeriod, selectedInfoObj, defaultTimeStamp, priceChangeValue, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const stockCache = useStockMeta(assetId);
    const isTradingAccountAlpaca = useAppSelector(selectIsTradingAccountAlpaca);
    const didMount = React.useRef(false);
    const currentPrice = useCurrentPrice(assetId);
    const balanceSymbol = React.useMemo(() => getSymbolFromCurrency(currentPrice?.currency || (isTradingAccountAlpaca ? 'USD' : 'GBP')) ||
        (isTradingAccountAlpaca ? '$' : '£'), [currentPrice?.currency, isTradingAccountAlpaca]);
    const [isLongPressGestureActive, setIsLongPressGestureActive] = React.useState(false);
    const [fallbackInfo, setFallbackInfo] = React.useState(getFallbackInfo(currentPrice?.value || 0, balanceSymbol));
    React.useEffect(() => {
        if (didMount.current) {
            if (!isLongPressGestureActive) {
                setFallbackInfo(getFallbackInfo(currentPrice?.value || 0, balanceSymbol));
            }
        }
        else {
            didMount.current = true;
        }
    }, [balanceSymbol, currentPrice?.value, isLongPressGestureActive]);
    const infoSharedValue = useDerivedValue(() => {
        if (selectedInfoObj.value) {
            runOnJS(setIsLongPressGestureActive)(true);
            const valueToUse = 'originalY' in selectedInfoObj.value ? selectedInfoObj.value.originalY : selectedInfoObj.value.priceCloseRaw;
            const amount = formatAmountWorklet(Math.abs(valueToUse), '');
            const sign = valueToUse < 0 ? '-' : '';
            const [decimal, fraction] = amount.split('.');
            return {
                decimal,
                fraction: `.${fraction}`,
                currencyAndSign: `${sign}${balanceSymbol}`,
                currencyAndSignWidth: sign ? signPlusSymbolWidth : symbolWidth,
            };
        }
        runOnJS(setIsLongPressGestureActive)(false);
        return {
            decimal: fallbackInfo.decimal,
            fraction: fallbackInfo.fraction,
            currencyAndSign: fallbackInfo.currencyAndSign,
            currencyAndSignWidth: fallbackInfo.currencyAndSignWidth,
        };
    }, [
        balanceSymbol,
        fallbackInfo.currencyAndSign,
        fallbackInfo.currencyAndSignWidth,
        fallbackInfo.decimal,
        fallbackInfo.fraction,
        selectedInfoObj.value,
    ]);
    // On web the three text inputs below don't size to their text, so the price
    // is mirrored into state and drawn as text instead
    const [webSelectedPrice, setWebSelectedPrice] = React.useState();
    useDerivedValue(() => {
        if (isWeb) {
            let selectedPrice;
            if (selectedInfoObj.value) {
                selectedPrice =
                    'originalY' in selectedInfoObj.value ? selectedInfoObj.value.originalY : selectedInfoObj.value.priceCloseRaw;
            }
            runOnJS(setWebSelectedPrice)(selectedPrice);
        }
    }, [selectedInfoObj]);
    const decimalProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.decimal,
    }), [infoSharedValue]);
    const currencyProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.currencyAndSign,
        ...(isAndroid
            ? {
                width: infoSharedValue.value.currencyAndSignWidth,
            }
            : {}),
    }), [infoSharedValue]);
    const fractionProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.fraction,
    }), [infoSharedValue]);
    const timeProps = useAnimatedProps(() => {
        if (selectedInfoObj.value) {
            return { text: 'originalY' in selectedInfoObj.value ? selectedInfoObj.value.timeStamp : '' };
        }
        return {
            text: defaultTimeStamp,
        };
    }, [defaultTimeStamp, selectedInfoObj]);
    const time = React.useMemo(() => (<AnimatedTextInput editable={false} numberOfLines={1} style={styles.time} allowFontScaling={false} animatedProps={timeProps} defaultValue={defaultTimeStamp}/>), [defaultTimeStamp, styles.time, timeProps]);
    const isESG = stockCache?.fundTheme === 'ESG';
    return (<View style={styles.container}>
      <View style={styles.row}>
        <View style={styles.flex}>
          {isESG ? (<View style={styles.esgContainer}>
              <IconLeaf color={colors.sectors.esg} width={16} height={16}/>
              <Text color={colors.sectors.esg} Text-12>
                ESG
              </Text>
            </View>) : null}
          <Text Text-14 Gray numberOfLines={2}>
            {!isTradingAccountAlpaca
            ? stockCache?.name ?? ''
            : `${stockCache?.name ?? ''}${stockCache?.symbol ? ` ($${stockCache.symbol})` : ''}`}
          </Text>
          <Spacer h={2}/>
          {isWeb ? (<CurrencyAmountText size="L" value={{
                amount: webSelectedPrice ?? currentPrice?.value ?? 0,
                currency: currentPrice?.currency || (isTradingAccountAlpaca ? 'USD' : 'GBP'),
            }}/>) : (<View style={styles.amountView}>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={currencyProps} defaultValue={fallbackInfo.currencyAndSign} style={[styles.currencyStyles, isAndroid && { width: fallbackInfo.currencyAndSignWidth }]}/>
              <Spacer w={2}/>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={decimalProps} style={styles.absAmountStyles} defaultValue={fallbackInfo.decimal}/>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={fractionProps} style={styles.fractionStyles} defaultValue={fallbackInfo.fraction}/>
            </View>)}
        </View>
        <Spacer w={16}/>
        <Logo R46 uri={stockCache?.iconUrl} resizeMode="contain"/>
      </View>
      <View style={styles.row}>
        <StockInfoPriceChangeView assetId={assetId} priceTrend={priceTrend} selectedPeriod={selectedPeriod} selectedInfoObj={selectedInfoObj} priceChangeValue={priceChangeValue}/>
        {time}
      </View>
    </View>);
};
export default React.memo(StockInfoView);
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingHorizontal: rem(16),
    },
    row: {
        flexDirection: 'row',
    },
    time: {
        margin: 0,
        padding: 0,
        ...textStyles.TextThin_14,
        color: colors.text.secondary,
    },
    amountView: {
        flexDirection: 'row',
        paddingBottom: rem(2),
        // Android stretches each input to the row's height and centres its text,
        // so different font sizes end up on different baselines. Yoga can align
        // the inputs' baselines directly instead.
        ...(isAndroid && { alignItems: 'baseline' }),
    },
    currencyStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(30),
        lineHeight: rem(41),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    absAmountStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(36),
        lineHeight: rem(48),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    fractionStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(24),
        lineHeight: rem(38),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    flex: {
        flex: 1,
    },
    esgContainer: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(4),
        paddingHorizontal: rem(8),
        paddingVertical: rem(4),
        backgroundColor: 'rgba(105, 226, 145, 0.16)',
        borderRadius: rem(6),
        marginBottom: rem(8),
    },
}));
